import React from 'react';
import SEO from '../components/SEO';

const PrivacyPolicy = () => {
  return (
    <>
      <SEO 
        title="Privacy Policy | Prestige Trademark"
        description="Prestige Trademark Privacy Policy. Learn how we collect, use, and protect your personal information."
        keywords="privacy policy, data protection, personal information"
        canonical="/privacy-policy"
      />
      <div className="min-h-screen py-20 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-4xl md:text-5xl font-bold text-center mb-8">Privacy Policy</h1>
          <p className="text-gray-600 text-center mb-12">Last Updated: November 2024</p>
          
          <article className="prose prose-lg max-w-none bg-white p-8 rounded-lg shadow-sm">
            <h2>Introduction</h2>
            <p>
              This Privacy Policy discloses the policies of Prestige Trademark (the "Website") and its operator Prestige Trademark (collectively "we" or "us") regarding the collection, use and disclosure of information you submit to us through the use of the Website.
            </p>
            <p>
              Prestige Trademark is part of an affiliation of companies owned by the same ownership group (collectively, "our Group Companies"). In some cases, we may receive personal information from our Group Companies, and we may share personal information with our Group Companies.
            </p>

            <h2>User Consent</h2>
            <p>
              By accessing or otherwise using the Website, you agree to the terms and conditions of this Privacy Policy, which is incorporated into and forms a part of the Website's Terms of Service. You expressly consent to the processing and use of your information as described herein.
            </p>

            <h2>Types of Information Collected</h2>
            <p>
              "Personal information" is data that identifies, relates to, describes, can be used to contact, or could reasonably be linked directly or indirectly to you. For purposes of this Policy, there is no meaningful distinction between the terms "personal information" and "personal data."
            </p>

            <h3>A. Personal Information We Collect Directly from You</h3>
            <p>As you visit or use our website, we collect the following categories of personal information directly from you:</p>
            <ul>
              <li>Your name</li>
              <li>Contact information (such as email address, postal address, phone number)</li>
              <li>Company details (such as the company name, officers, members, and associated addresses)</li>
              <li>Billing information (such as credit, debit, or other payment card information)</li>
              <li>Information required for registration (such as your citizenship status)</li>
              <li>Communications you send us (such as customer support and other requests)</li>
              <li>Other information you may provide about yourself or others through our Services</li>
            </ul>

            <h3>B. Personal Information Generated by Us</h3>
            <p>
              As you use our website we generate certain personal information about you, including through automatic data collection. We may automatically collect information about your interactions with the Website using technologies such as cookies, web beacons, and other tracking technologies.
            </p>
            <p>Specifically, we collect:</p>
            <ul>
              <li><strong>Device Information:</strong> IP address, user agent, hardware and software information, and device configuration</li>
              <li><strong>Usage Information:</strong> Pages viewed, time spent, clicks, and navigation patterns</li>
            </ul>

            <h2>Cookies and Tracking Technologies</h2>
            <p>
              Cookies are small pieces of data stored on your device when websites are loaded in a browser. We use first-party and third-party cookies for various purposes:
            </p>

            <h3>First Party Cookies</h3>
            <p>
              Prestige Trademark utilizes cookies, web beacons, and other technologies to personalize the Website for each user by remembering information about the user's visit.
            </p>

            <h3>Third Party Cookies</h3>
            <p>We work with third parties that place cookies on our website to provide:</p>
            <ul>
              <li><strong>Analytics/Measurement:</strong> We use third-party analytics cookies (including Google Analytics) to understand how visitors use the Website and to optimize our services</li>
              <li><strong>Advertising:</strong> We may use advertising cookies to deliver relevant ads and measure campaign effectiveness</li>
            </ul>

            <h2>Use and Disclosure of Personal Information</h2>
            <h3>General Use</h3>
            <p>We use your personal information to:</p>
            <ul>
              <li>Provide our trademark registration and related services</li>
              <li>Communicate with you about your orders and services</li>
              <li>Send you marketing communications (with your consent)</li>
              <li>Process payments and prevent fraud</li>
              <li>Comply with legal obligations</li>
              <li>Improve our Website and services</li>
            </ul>

            <h3>Disclosure to Third Parties</h3>
            <p>We do not sell your personal information. We may share your information with:</p>
            <ul>
              <li><strong>Group Companies:</strong> For integrated services and customer support</li>
              <li><strong>Service Providers:</strong> Third-party vendors who help us operate our business (payment processors, email service providers, etc.)</li>
              <li><strong>Government Agencies:</strong> As required by law, such as the USPTO for trademark filings</li>
              <li><strong>Legal Requirements:</strong> When necessary to comply with legal obligations or protect our rights</li>
            </ul>

            <h2>Storage and Protection of Your Information</h2>
            <p>
              We have implemented appropriate technical and organizational security measures to protect your personal information against unauthorized access, loss, or misuse. These measures include:
            </p>
            <ul>
              <li>SSL certificate and encryption protocols</li>
              <li>Secure payment processing through third-party processors</li>
              <li>Access controls and authentication</li>
              <li>Regular security assessments</li>
            </ul>
            <p>
              <strong>Credit Card Information:</strong> We do not store your credit card information directly. Payment processing is handled by secure third-party payment processors.
            </p>

            <h2>Your Rights and Choices</h2>
            <h3>Email Communications</h3>
            <p>
              You may opt-out of promotional emails by following the unsubscribe link in any marketing email we send you. Please note that you cannot opt-out of service-related communications.
            </p>

            <h3>Cookies</h3>
            <p>
              You can control cookies through your browser settings. However, disabling cookies may limit your ability to use certain features of our Website.
            </p>

            <h3>Your Legal Rights</h3>
            <p>Depending on your location, you may have the following rights:</p>
            <ul>
              <li>Access to your personal information</li>
              <li>Correction of inaccurate information</li>
              <li>Deletion of your information (subject to legal requirements)</li>
              <li>Restriction of processing</li>
              <li>Data portability</li>
              <li>Objection to processing</li>
            </ul>
            <p>
              To exercise these rights, please contact us at info@prestigetrademark.com.
            </p>

            <h2>Data Retention</h2>
            <p>
              We retain your personal information for as long as necessary to:
            </p>
            <ul>
              <li>Provide our services to you</li>
              <li>Comply with legal obligations</li>
              <li>Resolve disputes</li>
              <li>Enforce our agreements</li>
            </ul>

            <h2>Children's Privacy</h2>
            <p>
              Our Website is not intended for children under 16 years of age. We do not knowingly collect personal information from children under 16. If you become aware that a child has provided us with personal information, please contact us immediately.
            </p>

            <h2>Changes to This Privacy Policy</h2>
            <p>
              We may update this Privacy Policy from time to time. Any changes will be posted on this page with an updated "Last Updated" date. We encourage you to review this Privacy Policy periodically. Your continued use of the Website after changes are posted constitutes your acceptance of the updated Privacy Policy.
            </p>

            <h2>Contact Us</h2>
            <p>
              If you have any questions or concerns about this Privacy Policy or our data practices, please contact us:
            </p>
            <ul>
              <li><strong>Email:</strong> info@prestigetrademark.com</li>
              <li><strong>Phone:</strong> +1 (415) 854 3407</li>
            </ul>
            <p className="mt-8 text-sm text-gray-600">
              Your contact information will never be shared with third parties for marketing purposes unrelated to providing our services to you.
            </p>
          </article>
        </div>
      </div>
    </>
  );
};

export default PrivacyPolicy;